import React, { useEffect, useRef, useState } from 'react';
import Flatpickr from 'react-flatpickr';
import dayjs from 'dayjs';
import { Helmet } from 'react-helmet';
import './OrdersSearch.scss';
import { OrderClazz, OrderStatus } from '../../types/orderTypes';
import {
  fetchAllOrdersAsync,
  fetchAllOrdersByExternalIdAsync,
} from '../../fetch/orderFetch';
import { Loader } from '../../components/Loader/Loader';
import * as H from 'history';
import { getParametersFromQueryString } from '../../utils/urlUtil';
import Input from '../../components/inputs/Input';
import { Link } from 'react-router-dom';
import { browserHistory } from '../../browserHistory';
import { capitalize } from '../../utils/strings';
import { formatPhoneNumber } from '../../utils/formatting';

const SEARCH_WAIT_INTERVAL = 2000;
const DEFAULT_RESULTS_COUNT = 20;

declare interface OrdersProps {
  location: H.Location;
}

const orderStatus: {
  [status in OrderStatus]: string;
} = {
  accepted: 'Accepted',
  in_delivery: 'In delivery',
  delivered: 'Delivered',
  canceled: 'Canceled',
  ready: 'Ready',
};

const statusSortIdx: {
  [status in OrderStatus]: number;
} = {
  accepted: 1,
  in_delivery: 2,
  delivered: 3,
  canceled: 4,
  ready: 0,
};

export default function OrdersSearch({ location }: OrdersProps) {
  const searchQuery = decodeURIComponent(
    getParametersFromQueryString(location.search)['q'] || ''
  ).trim();
  const queryMode = decodeURIComponent(
    getParametersFromQueryString(location.search)['mode'] || 'text'
  );
  const [isLoading, setLoading] = useState<boolean>(searchQuery !== '');
  const [fetchedOrders, setFetchedOrders] = useState<OrderClazz[]>([]);
  const [mode, setMode] = useState(queryMode);
  const [currentSearch, setCurrentSearch] = useState(searchQuery);
  const [searchText, setSearchText] = useState(searchQuery);
  const [dateRange, setDateRange] = useState({
    startDate: dayjs().add(-7, 'day'),
    endDate: dayjs().add(+7, 'day'),
  });
  const [showResultsCount, setShowResultsCount] = useState<number>(
    DEFAULT_RESULTS_COUNT
  );
  const typingTimer = useRef<any>();

  useEffect(() => {
    setLoading(true);
    const text = currentSearch.trim();
    if (text !== '' && text.length > 2) {
      fetchOrders(text)
        .then(orders => {
          setShowResultsCount(DEFAULT_RESULTS_COUNT);
          setFetchedOrders(sortByStatus(orders));
          setLoading(false);
          browserHistory.push(
            `/orders/search?q=${encodeURIComponent(text)}&mode=${queryMode}`
          );
        })
        .catch(() => {
          setFetchedOrders([]);
          setLoading(false);
        });
    } else {
      setFetchedOrders([]);
      setLoading(false);
      browserHistory.push(`/orders/search`);
    }

    return () => clearTimeout(typingTimer.current);
  }, [dateRange, currentSearch]);

  function fetchOrders(text: string): Promise<OrderClazz[]> {
    if (mode == 'externalId') {
      return fetchAllOrdersByExternalIdAsync(text);
    }
    return fetchAllOrdersAsync(
      dateRange.startDate,
      dateRange.endDate,
      undefined,
      undefined,
      text
    );
  }

  function sortByStatus(orders: OrderClazz[]): OrderClazz[] {
    return orders.sort((o1, o2) => {
      return statusSortIdx[o1.status] - statusSortIdx[o2.status];
    });
  }

  function handleChange(search: string) {
    if (isLoading) {
      return;
    }
    clearTimeout(typingTimer.current);
    setSearchText(search);
    typingTimer.current = setTimeout(
      () => triggerSearch(),
      SEARCH_WAIT_INTERVAL
    );
  }

  function handleKeyPress(event) {
    if (event.key === 'Enter') {
      triggerSearch();
    }
  }

  function triggerSearch() {
    clearTimeout(typingTimer.current);
    setCurrentSearch('');
    setCurrentSearch(searchText);
  }

  function ChooseDate() {
    const onSelectedPeriod = (selectedDates: Date[]) => {
      if (selectedDates != null && selectedDates.length > 0) {
        const secondIndex = selectedDates.length === 2 ? 1 : 0;
        setDateRange({
          startDate: dayjs(selectedDates[0]).clone(),
          endDate: dayjs(selectedDates[secondIndex]).clone(),
        });
      }
    };

    const checkSelectedPeriod = (selectedDates, datePicker) => {
      if (selectedDates != null && selectedDates.length === 1) {
        const startDate = dayjs(selectedDates[0]);
        const maxDate = startDate.add(14, 'day');
        const minDate = startDate.subtract(14, 'day');
        datePicker.set('minDate', minDate.toISOString());
        datePicker.set('maxDate', maxDate.toISOString());
        datePicker.jumpToDate(minDate.toISOString(), true);
      }
    };

    const defaultRange = () => {
      return [dateRange.startDate.toDate(), dateRange.endDate.toDate()];
    };

    return (
      <div>
        <Flatpickr
          onChange={(selectedDates, representation, instance) =>
            checkSelectedPeriod(selectedDates, instance)
          }
          onClose={selectedDates => onSelectedPeriod(selectedDates)}
          options={{
            dateFormat: 'l d M',
            defaultDate: defaultRange(),
            mode: 'range',
            showMonths: window.innerWidth > 700 ? 2 : 1,
            shorthandCurrentMonth: true,
            locale: { firstDayOfWeek: 1 },
          }}
          render={(props, ref) => (
            <input
              className="form-control orders-search__menubar-search-input"
              type="text"
              ref={ref}
            />
          )}
        />
      </div>
    );
  }

  const moreToShow = fetchedOrders.length > showResultsCount;

  const externalIdMode = mode == 'externalId';

  return (
    <section className="orders-search">
      <Helmet>
        <title>Orders search - {searchText}</title>
      </Helmet>
      <h1>ORDERS SEARCH</h1>
      <div className="orders-search__content">
        <div className="orders-search__menubar">
          <Input
            className="orders-search__menubar-search"
            placeholder={
              externalIdMode ? 'reference or parcel id' : 'Search...'
            }
            onChange={event => handleChange(event.target.value)}
            onKeyPress={handleKeyPress}
            value={searchText}
            autoFocus={true}
          />
          {!externalIdMode && <ChooseDate />}
        </div>
        {isLoading && (
          <div className="orders-search__loader">
            <Loader size="xxl" type="tile" />
          </div>
        )}

        {!isLoading &&
          currentSearch.trim() !== '' &&
          fetchedOrders.length === 0 && <h2>No matching orders were found.</h2>}

        {!isLoading && currentSearch.trim() !== '' && fetchedOrders.length > 0 && (
          <div className="orders-search__results">
            <div className="orders-search__results-count">
              {fetchedOrders.length} orders found
            </div>
            <table className="orders-search__table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>STATUS</th>
                  <th>REGION</th>
                  <th>PICKUP</th>
                  <th>RECIPIENT</th>
                  <th>EMAIL</th>
                  <th>ADDRESS</th>
                  <th>PHONE</th>
                  <th>DATE</th>
                  <th>WINDOW</th>
                </tr>
              </thead>
              <tbody>
                {fetchedOrders.slice(0, showResultsCount).map(order => (
                  <tr key={order.id}>
                    <td>
                      <Link to={`/orders/${order.id}`}>{order.id}</Link>
                    </td>
                    <td>{orderStatus[order.status]}</td>
                    <td>{capitalize(order.region)}</td>
                    <td>{order.pickupName}</td>
                    <td>{order.recipientName}</td>
                    <td>{order.recipientEmail}</td>
                    <td>{order.destinationAddress.fullAddressString}</td>
                    <td>{formatPhoneNumber(order.recipientPhone)}</td>
                    <td>{order.deliverFromDate.format('DD MMM')}</td>
                    <td>{order.windowTime()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {moreToShow && (
              <div className="orders-search__results-more">
                <span
                  onClick={() => setShowResultsCount(showResultsCount + 20)}
                >
                  Show more results...
                </span>
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
